/**
 * What to do when an upload collides with names already in the folder.
 *
 * The backend answers a colliding upload with 409 and the list of offending
 * names rather than guessing, so the choice is the user's:
 *
 * - **Replace** — the existing file gains a new version. Nothing is lost;
 *   the previous bytes stay in its version history.
 * - **Keep both** — the upload is stored under a numbered name.
 * - **Cancel** — nothing is uploaded.
 *
 * Closing is blocked while a resolution is in flight, so a dismissed dialog
 * cannot leave a half-finished upload with no way to see how it ended.
 */

import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import LoadingSpinner from '@/shared/ui/LoadingSpinner.jsx';

export function UploadConflictDialog({ open, conflicts, resolving, onCancel, onKeepBoth, onReplace }) {
  const { t } = useTranslation(['my-docs']);

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next && !resolving) onCancel();
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('my-docs:create.conflict.title') || 'Files already exist'}</DialogTitle>
        </DialogHeader>

        <p className="text-sm text-muted-foreground">
          {t('my-docs:create.conflict.body') || 'These names already exist in this folder:'}
        </p>

        <ul className="max-h-40 overflow-auto rounded-md border border-border bg-muted/30 p-2 text-sm">
          {conflicts.map((conflict) => (
            <li key={conflict.name} className="truncate px-1 py-0.5 font-medium">
              {conflict.name}
            </li>
          ))}
        </ul>

        <p className="text-xs text-muted-foreground">
          {t('my-docs:create.conflict.hint') ||
            'Replace overwrites the existing file as a new version. Keep both adds a numbered copy.'}
        </p>

        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="outline" disabled={resolving} onClick={onCancel}>
            {t('my-docs:create.conflict.cancel') || 'Cancel'}
          </Button>
          <Button variant="outline" disabled={resolving} onClick={onKeepBoth}>
            {t('my-docs:create.conflict.keepBoth') || 'Keep both'}
          </Button>
          <Button
            disabled={resolving}
            className="text-white border-none"
            style={{ background: 'linear-gradient(135deg, #4185F6 0%, #6FA4F9 100%)' }}
            onClick={onReplace}
          >
            {resolving ? (
              <LoadingSpinner color="#fff" />
            ) : (
              t('my-docs:create.conflict.replace') || 'Replace'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
